import type { DataItemId } from '@pn/core/domain/data';
import { Frequency } from '@pn/core/domain/production/frequency';
import { defaultWellFluids } from '@pn/core/domain/settings';
import type { UnitSystem } from '@pn/core/domain/types';
import { convertUnit, type SIUnit } from '@pn/core/domain/units';
import { isNil, isString, sortBy } from 'lodash-es';

export * from './derivatives';
export * from './frequency';

export type ProductionItem = {
  date: string;
  flowType: 'production' | 'injection' | 'other';
  fluid: string; // e.g. 'Oil', 'Gas', etc.
  producingDaily: SIUnit;
  calendarDaily: SIUnit;
  monthly: SIUnit;
  cumulative: SIUnit;
  hours: number | undefined;
  count: number | undefined;
  id: DataItemId | undefined;
};

export function convertProductionItem(
  productionItem: ProductionItem,
  unitSystem: UnitSystem
): ProductionItem {
  return {
    ...productionItem,
    producingDaily: convertUnit(productionItem.producingDaily, unitSystem, 2),
    calendarDaily: convertUnit(productionItem.calendarDaily, unitSystem, 2),
    monthly: convertUnit(productionItem.monthly, unitSystem, 2),
    cumulative: convertUnit(productionItem.cumulative, unitSystem, 2),
  };
}

export function toFrequencyLabel(frequency: Frequency): string {
  switch (frequency) {
    case Frequency.ProducingDaily:
      return 'Producing Daily';
    case Frequency.CalendarDaily:
      return 'Calendar Daily';
    case Frequency.Monthly:
      return 'Monthly';
    case Frequency.Cumulative:
      return 'Cumulative';
  }
}

export type GroupedProductionItem = {
  date: string;
  hours: number | undefined;
  count: number | undefined;
  id: DataItemId; // empty string for aggregate production
  [Frequency.ProducingDaily]: Record<ProductionItem['fluid'], SIUnit>;
  [Frequency.CalendarDaily]: Record<ProductionItem['fluid'], SIUnit>;
  [Frequency.Monthly]: Record<ProductionItem['fluid'], SIUnit>;
  [Frequency.Cumulative]: Record<ProductionItem['fluid'], SIUnit>;
};

export type ProductionChartType =
  | 'individual'
  | 'lease'
  | 'list_aggregate'
  | 'list_compare'
  | 'aggregate'
  | 'compare';
export function isProductionChartType(
  arg: unknown
): arg is ProductionChartType {
  return (
    isString(arg) &&
    [
      'individual',
      'lease',
      'list_aggregate',
      'list_compare',
      'aggregate',
      'compare',
    ].includes(arg)
  );
}

export type FluidSymbolPair = {
  fluid: string;
  symbol: string;
};

export function getFluidSymbolPairs(
  productionItems: ProductionItem[],
  frequency: Frequency
): FluidSymbolPair[] {
  // 1. Build unique & alphabetically sorted list
  const pairMap = new Map<string, FluidSymbolPair>();

  for (const item of productionItems) {
    pairMap.set(item.fluid, {
      fluid: item.fluid,
      symbol: item[frequency].symbol,
    });
  }

  const sorted = sortBy(Array.from(pairMap.values()), (v) => v.fluid);

  // 2. Define special ordering
  const frontOrder = defaultWellFluids;
  const backOrder = ['Water Cut', 'GOR', 'WGR', 'CGR'];

  // 3. Pick out front/back, and leave the middle as-is
  const front = frontOrder
    .map((label) => sorted.find((g) => g.fluid === label))
    .filter((g) => !isNil(g));

  const back = backOrder
    .map((label) => sorted.find((g) => g.fluid === label))
    .filter((g) => !isNil(g));

  const middle = sorted.filter(
    (g) => !frontOrder.includes(g.fluid) && !backOrder.includes(g.fluid)
  );

  return [...front, ...middle, ...back];
}

export function hasProduction(
  dataType: string | undefined
): dataType is 'wells' | 'wells_usa' {
  return dataType === 'wells' || dataType === 'wells_usa';
}

// tabs within the unified full-page production shell (?view= search param)
export type ProductionView = 'chart' | 'forecast' | 'type-curves';
export function isProductionView(arg: unknown): arg is ProductionView {
  return isString(arg) && ['chart', 'forecast', 'type-curves'].includes(arg);
}

/**
 * List/aggregate/compare production chart types that represent more than one well,
 * i.e. ones a per-well forecast walkthrough can be built from.
 */
export function isMultiWellProductionChartType(
  productionChartType: ProductionChartType
): productionChartType is
  | 'list_aggregate'
  | 'list_compare'
  | 'aggregate'
  | 'compare' {
  return ['list_aggregate', 'list_compare', 'aggregate', 'compare'].includes(
    productionChartType
  );
}

export function isListDrivenProductionChartType(
  productionChartType: ProductionChartType
): boolean {
  return productionChartType.startsWith('list');
}

export function isAggregateProductionChartType(
  productionChartType: ProductionChartType
): productionChartType is 'list_aggregate' | 'aggregate' {
  return (
    productionChartType === 'list_aggregate' ||
    productionChartType === 'aggregate'
  );
}

export function isCompareProductionChartType(
  productionChartType: ProductionChartType
): productionChartType is 'list_compare' | 'compare' {
  return (
    productionChartType === 'list_compare' || productionChartType === 'compare'
  );
}

/** The aggregate sibling of a chart type; list-driven contexts stay list-driven. */
export function getAggregateProductionChartType(
  productionChartType: ProductionChartType
): 'list_aggregate' | 'aggregate' {
  return isListDrivenProductionChartType(productionChartType)
    ? 'list_aggregate'
    : 'aggregate';
}

/** The compare sibling of a chart type; list-driven contexts stay list-driven. */
export function getCompareProductionChartType(
  productionChartType: ProductionChartType
): 'list_compare' | 'compare' {
  return isListDrivenProductionChartType(productionChartType)
    ? 'list_compare'
    : 'compare';
}

export const COMPARE_ITEM_ID = 'compare-temporary-item'; // workspace item in Production Compare mode
